import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { CreditCard, Smartphone, QrCode, CheckCircle, XCircle, Clock, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function OrangeMoneyPayment({ invoice, onPaymentSuccess, onCancel }) {
  const [loading, setLoading] = useState(false);
  const [customerPhone, setCustomerPhone] = useState('');
  const [paymentData, setPaymentData] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);

  useEffect(() => {
    // Nettoyer l'intervalle lors du démontage
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  const handleInitiatePayment = async (e) => {
    e.preventDefault();
    
    // Validation du numéro de téléphone
    if (!customerPhone.startsWith('+224')) {
      toast.error('Le numéro doit commencer par +224');
      return;
    }

    if (customerPhone.length !== 13) {
      toast.error('Format invalide. Exemple: +224621234567');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/payments/orange-money/initiate`,
        {
          invoice_id: invoice.id,
          customer_phone: customerPhone
        }
      );

      setPaymentData(response.data);
      toast.success('Paiement initié ! Composez le code USSD ou scannez le QR code.');

      // Démarrer le polling du statut
      startStatusPolling(response.data.transaction_id);
    } catch (error) {
      console.error('Error initiating payment:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'initiation du paiement');
    } finally {
      setLoading(false);
    }
  };

  const startStatusPolling = (transactionId) => {
    // Vérifier le statut toutes les 3 secondes
    const interval = setInterval(async () => {
      try {
        const response = await axios.get(
          `${API}/payments/orange-money/status/${transactionId}`
        );

        const status = response.data.transaction_status;
        setPaymentStatus(status);

        if (status === 'SUCCESS') {
          clearInterval(interval);
          toast.success('Paiement réussi !');
          setTimeout(() => {
            onPaymentSuccess && onPaymentSuccess();
          }, 2000);
        } else if (status === 'FAILED' || status === 'EXPIRED') {
          clearInterval(interval);
          toast.error(`Paiement ${status === 'FAILED' ? 'échoué' : 'expiré'}`);
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
      }
    }, 3000);

    setPollingInterval(interval);
  };

  const handleSimulatePayment = async (success) => {
    if (!paymentData) return;

    try {
      await axios.post(
        `${API}/payments/orange-money/simulate/${paymentData.transaction_id}`,
        null,
        { params: { success } }
      );

      if (success) {
        toast.success('Paiement simulé avec succès !');
      } else {
        toast.error('Paiement simulé comme échoué');
      }
    } catch (error) {
      console.error('Error simulating payment:', error);
      toast.error('Erreur lors de la simulation');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'SUCCESS':
        return <CheckCircle className="w-16 h-16 text-green-500" />;
      case 'FAILED':
        return <XCircle className="w-16 h-16 text-red-500" />;
      case 'EXPIRED':
        return <AlertTriangle className="w-16 h-16 text-orange-500" />;
      case 'PENDING':
      default:
        return <Clock className="w-16 h-16 text-blue-500 animate-pulse" />;
    }
  };

  const getStatusMessage = (status) => {
    switch (status) {
      case 'SUCCESS':
        return 'Paiement effectué avec succès !';
      case 'FAILED':
        return 'Le paiement a échoué. Veuillez réessayer.';
      case 'EXPIRED':
        return 'La transaction a expiré. Veuillez réessayer.';
      case 'PENDING':
      default:
        return 'En attente de confirmation...';
    }
  };

  if (!paymentData) {
    // Formulaire d'initiation du paiement
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center mb-4">
          <div className="bg-orange-100 p-4 rounded-full">
            <Smartphone className="w-12 h-12 text-orange-600" />
          </div>
        </div>

        <div className="text-center space-y-2">
          <h3 className="text-xl font-bold">Paiement Orange Money</h3>
          <p className="text-gray-600">
            Payez votre facture de <span className="font-bold text-orange-600">{invoice.total_amount.toLocaleString()} GNF</span> via Orange Money
          </p>
        </div>

        <form onSubmit={handleInitiatePayment} className="space-y-4">
          <div>
            <Label htmlFor="phone">Numéro Orange Money</Label>
            <Input
              id="phone"
              type="tel"
              placeholder="+224 6XX XX XX XX"
              value={customerPhone}
              onChange={(e) => setCustomerPhone(e.target.value)}
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Format: +224 6XX XX XX XX (ex: +224 621 23 45 67)
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2">
              Comment ça marche ?
            </h4>
            <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
              <li>Entrez votre numéro Orange Money</li>
              <li>Cliquez sur "Initier le paiement"</li>
              <li>Composez le code USSD sur votre téléphone</li>
              <li>Suivez les instructions et validez avec votre code PIN</li>
              <li>Attendez la confirmation</li>
            </ol>
          </div>

          <div className="flex space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="flex-1"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-orange-600 hover:bg-orange-700"
            >
              {loading ? 'Traitement...' : 'Initier le paiement'}
            </Button>
          </div>
        </form>
      </div>
    );
  }

  // Interface de confirmation du paiement
  return (
    <div className="space-y-6">
      <div className="flex flex-col items-center justify-center space-y-4">
        {getStatusIcon(paymentStatus)}
        
        <div className="text-center">
          <h3 className="text-xl font-bold mb-2">{getStatusMessage(paymentStatus)}</h3>
          <Badge className={
            paymentStatus === 'SUCCESS' ? 'bg-green-100 text-green-800' :
            paymentStatus === 'FAILED' ? 'bg-red-100 text-red-800' :
            paymentStatus === 'EXPIRED' ? 'bg-orange-100 text-orange-800' :
            'bg-blue-100 text-blue-800'
          }>
            {paymentStatus || 'PENDING'}
          </Badge>
        </div>
      </div>

      {paymentStatus === 'PENDING' && (
        <div className="space-y-4">
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Smartphone className="w-5 h-5 text-orange-600 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-semibold text-orange-900 mb-1">Code USSD</h4>
                <p className="text-sm text-orange-800 mb-2">
                  Composez ce code sur votre téléphone Orange Money :
                </p>
                <div className="bg-white p-3 rounded border border-orange-300">
                  <code className="text-lg font-mono font-bold text-orange-900">
                    {paymentData.ussd_code}
                  </code>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <QrCode className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-semibold text-blue-900 mb-1">QR Code</h4>
                <p className="text-sm text-blue-800 mb-2">
                  Ou scannez ce QR code avec l'application Orange Money
                </p>
                <a 
                  href={paymentData.qr_code_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 underline"
                >
                  Afficher le QR Code
                </a>
              </div>
            </div>
          </div>

          <div className="text-center text-sm text-gray-600">
            Transaction expire dans {Math.floor(paymentData.expires_in / 60)} minutes
          </div>

          {/* Boutons de simulation (pour tests uniquement) */}
          <div className="border-t pt-4">
            <p className="text-xs text-gray-500 mb-2 text-center">
              Mode Test : Simuler le paiement
            </p>
            <div className="flex space-x-2">
              <Button
                size="sm"
                variant="outline"
                className="flex-1 border-green-300 text-green-700 hover:bg-green-50"
                onClick={() => handleSimulatePayment(true)}
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                Succès
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="flex-1 border-red-300 text-red-700 hover:bg-red-50"
                onClick={() => handleSimulatePayment(false)}
              >
                <XCircle className="w-4 h-4 mr-1" />
                Échec
              </Button>
            </div>
          </div>
        </div>
      )}

      {(paymentStatus === 'SUCCESS' || paymentStatus === 'FAILED' || paymentStatus === 'EXPIRED') && (
        <div className="flex justify-center">
          <Button
            onClick={paymentStatus === 'SUCCESS' ? onPaymentSuccess : onCancel}
            className={paymentStatus === 'SUCCESS' ? 'bg-green-600 hover:bg-green-700' : ''}
          >
            {paymentStatus === 'SUCCESS' ? 'Voir la facture' : 'Fermer'}
          </Button>
        </div>
      )}
    </div>
  );
}

export default OrangeMoneyPayment;
